import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import CommonDataTable from '../../../components/CommonDataTable';
import { fetchItemInventory, refreshWMSAPI } from '../../../services/item/item';
import { getItemLocationFocus, subscribeItemLocationFocus, clearItemLocationFocus } from './itemLocationFocus';
import { readInventoryField } from '../inventoryFieldValue';

const firstValue = (...values) => {
  for (const value of values) {
    if (value !== undefined && value !== null && value !== '') return value;
  }
  return '';
};

const safeNumber = (value) => {
  if (value === undefined || value === null || value === '') return 0;
  const amount = typeof value === 'number' ? value : Number(String(value).replace(/,/g, '').trim());
  return Number.isFinite(amount) ? amount : 0;
};

const readQty = (item, ...aliases) => safeNumber(readInventoryField(item, ...aliases));

const mapInventoryRow = (item, index) => ({
  id: item._id || `${item.skuId || item.SKU || 'item'}-${item.location || item.LOC || index}`,
  owner: item.owner ?? '',
  item: firstValue(item.skuId, item.SKU, item.sku, item.item, readInventoryField(item, 'skuId', 'SKU', 'sku')),
  description: item.itemName ?? item.description ?? '',
  location: firstValue(item.location, item.LOC, item.loc, readInventoryField(item, 'location', 'LOC', 'loc')),
  onHand: readQty(item, 'stockOnHand', 'StockOnHand', 'onHand'),
  available: readQty(item, 'availableQty', 'available', 'Available'),
  allocated: readQty(item, 'qtyAllocated', 'QTYALLOCATED', 'allocated'),
  picked: readQty(item, 'qtyPicked', 'QTYPICKED', 'picked', 'pickedQty'),
  onHold: readQty(item, 'onHold', 'OnHold'),
  locationType: readInventoryField(item, 'locationType', 'locType', 'LOCATIONTYPE'),
  packKey: readInventoryField(item, 'packKey', 'PACKKEY', 'packkey', 'pack'),
  packDescription: readInventoryField(
    item,
    'packDescription',
    'packDescr',
    'PACKDESCR',
    'packdescription',
    'packKeyDescription',
    'pack_description'
  ),
});

const skuLocationKey = (row) => `${String(row?.item ?? '').trim()}\u0000${String(row?.location ?? '').trim()}`;

const aggregateBySkuAndLocation = (rows) => {
  const groups = new Map();

  rows.forEach((row) => {
    const key = skuLocationKey(row);
    const current = groups.get(key);
    if (!current) {
      groups.set(key, {
        ...row,
        id: key,
        onHand: safeNumber(row.onHand),
        available: safeNumber(row.available),
        allocated: safeNumber(row.allocated),
        picked: safeNumber(row.picked),
        onHold: safeNumber(row.onHold),
      });
      return;
    }

    current.onHand += safeNumber(row.onHand);
    current.available += safeNumber(row.available);
    current.allocated += safeNumber(row.allocated);
    current.picked += safeNumber(row.picked);
    current.onHold += safeNumber(row.onHold);
  });

  return Array.from(groups.values());
};

const sameValue = (left, right) => String(left ?? '').trim() === String(right ?? '').trim();

const compareOwnerThenSku = (left, right) => {
  const byOwner = String(left?.owner ?? '').localeCompare(String(right?.owner ?? ''), undefined, { numeric: true, sensitivity: 'base' });
  if (byOwner !== 0) return byOwner;
  return String(left?.item ?? '').localeCompare(String(right?.item ?? ''), undefined, { numeric: true, sensitivity: 'base' });
};

const rowMatchesFocus = (row, focus) => {
  if (!focus) return true;
  if (focus.item && !sameValue(row.item, focus.item)) return false;
  if (focus.location && !sameValue(row.location, focus.location)) return false;
  return true;
};

const applyTableFilters = (rows, filters) => {
  if (!Array.isArray(filters) || filters.length === 0) return rows;
  return rows.filter((row) => filters.every((rule) => {
    if (!rule?.columnId || rule.value == null || String(rule.value).trim() === '') return true;
    const raw = row[rule.columnId];
    const operator = rule.operator || 'contains';

    if (operator === '=' || operator === '>' || operator === '<' || operator === '>=' || operator === '<=') {
      const amount = Number(raw);
      const expectedAmount = Number(rule.value);
      if (!Number.isFinite(amount) || !Number.isFinite(expectedAmount)) return false;
      if (operator === '=') return amount === expectedAmount;
      if (operator === '>') return amount > expectedAmount;
      if (operator === '<') return amount < expectedAmount;
      if (operator === '>=') return amount >= expectedAmount;
      return amount <= expectedAmount;
    }

    const value = String(raw ?? '').toLowerCase();
    const expected = String(rule.value).toLowerCase();
    if (operator === 'equals') return value === expected;
    if (operator === 'startsWith') return value.startsWith(expected);
    if (operator === 'endsWith') return value.endsWith(expected);
    return value.includes(expected);
  }));
};

const LOCATION_SUMMARY_CAT = 'location_summary';

const fetchAllInventoryRows = async (filters = []) => {
  const size = 100;
  let pageNo = 0;
  let collected = [];
  let total = 0;

  do {
    const res = await fetchItemInventory(pageNo, size, filters, LOCATION_SUMMARY_CAT);
    const list = Array.isArray(res?.data) ? res.data : [];
    total = Number(res?.totalItems ?? list.length);
    collected = collected.concat(list);
    pageNo += 1;
    if (list.length === 0) break;
  } while (collected.length < total && pageNo < 30);

  return collected;
};

const rawColumns = [
  { field: 'location', headerName: 'Location', width: 140 },
  { field: 'owner', headerName: 'Owner', width: 140 },
  { field: 'item', headerName: 'SKU ID', width: 160 },
  { field: 'description', headerName: 'Description', width: 220 },
  { field: 'onHand', headerName: 'On Hand', width: 110, type: 'number' },
  { field: 'available', headerName: 'Available', width: 110, type: 'number' },
  { field: 'allocated', headerName: 'Allocated', width: 110, type: 'number' },
  { field: 'picked', headerName: 'Picked', width: 110, type: 'number' },
  { field: 'onHold', headerName: 'On Hold', width: 110, type: 'number' },
  { field: 'locationType', headerName: 'Location Type', width: 140 },
  { field: 'packKey', headerName: 'Pack Key', width: 130 },
  { field: 'packDescription', headerName: 'Pack Description', width: 180 },
];

export default function ItemLocationList() {
  const [tableRows, setTableRows] = useState([]);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [appliedFilters, setAppliedFilters] = useState([]);
  const [syncing, setSyncing] = useState(false);
  const [focusVersion, setFocusVersion] = useState(0);
  const aggregatedCacheRef = useRef(null);
  const requestIdRef = useRef(0);

  const fetchHandler = useCallback(async (pageNo, pageSizeNo, filters = [], options = {}) => {
    const focus = getItemLocationFocus();
    const filterKey = JSON.stringify(Array.isArray(filters) ? filters : []);
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;

    try {
      if (
        options.refresh ||
        !aggregatedCacheRef.current ||
        aggregatedCacheRef.current.filterKey !== filterKey
      ) {
        const list = await fetchAllInventoryRows(filters);
        if (requestId !== requestIdRef.current) return;
        aggregatedCacheRef.current = {
          filterKey,
          rows: aggregateBySkuAndLocation(list.map(mapInventoryRow)),
        };
      }

      const matched = [...applyTableFilters(
        aggregatedCacheRef.current.rows.filter((row) => rowMatchesFocus(row, focus)),
        filters
      )].sort(compareOwnerThenSku);
      const start = pageNo * pageSizeNo;
      setTableRows(matched.slice(start, start + pageSizeNo));
      setTotalCount(matched.length);
    } catch (error) {
      if (requestId !== requestIdRef.current) return;
      console.error('Error fetching Location Summary data:', error);
    }
  }, []);

  useEffect(() => subscribeItemLocationFocus(() => {
    setPage(0);
    setFocusVersion((version) => version + 1);
  }), []);

  useEffect(() => {
    fetchHandler(page, pageSize, appliedFilters);
  }, [fetchHandler, page, pageSize, appliedFilters, focusVersion]);

  const handleDataRefresh = useCallback((payload = {}) => {
    const nextFilters = Array.isArray(payload.filters) ? payload.filters : [];
    const nextSize = Number(payload.pageSize);
    setAppliedFilters(nextFilters);
    if (Number.isFinite(nextSize) && nextSize > 0 && nextSize !== pageSize) {
      setPageSize(nextSize);
    }
    setPage(0);
  }, [pageSize]);

  const handleSyncWms = useCallback(async () => {
    if (syncing) return;

    try {
      setSyncing(true);
      await refreshWMSAPI();
      await fetchHandler(page, pageSize, appliedFilters, { refresh: true });
    } catch (error) {
      console.error('WMS Sync Error:', error);
      alert(
        error?.response?.data?.message ||
        'Failed to sync item inventory from WMS'
      );
    } finally {
      setSyncing(false);
    }
  }, [syncing, fetchHandler, page, pageSize, appliedFilters]);

  const allPossibleColumns = useMemo(() => rawColumns, []);
  const initialVisible = useMemo(() => allPossibleColumns.map((column) => column.field), [allPossibleColumns]);

  return (
    <CommonDataTable
      id="LocationSummary"
      allPossibleColumns={allPossibleColumns}
      initialVisible={initialVisible}
      initialRows={tableRows}
      height={520}
      rowsPerPageOptions={[10, 25, 50, 100]}
      exportImportPath="ItemInventory"
      exportQueryParams={{ cat: LOCATION_SUMMARY_CAT }}
      forItems
      forInventory
      enableInlineColumnFilters
      showSyncWms
      onSyncWms={handleSyncWms}
      page={page}
      pageSize={pageSize}
      rowCount={totalCount}
      onPageChange={(newPage) => setPage(newPage)}
      onPageSizeChange={(newSize) => {
        setPageSize(newSize);
        setPage(0);
      }}
      onDataRefresh={handleDataRefresh}
      onManualRefresh={(payload) => {
        clearItemLocationFocus();
        aggregatedCacheRef.current = null;
        setPage(0);
        return fetchHandler(
          0,
          payload?.pageSize ?? pageSize,
          payload?.filters ?? appliedFilters,
          { refresh: true }
        );
      }}
    />
  );
}
